import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/users")({
	component: AdminUsers,
});

const adminClient = createClient(AdminService, transport);

function AdminUsers() {
	const { t } = useTranslation("admin");
	useBreadcrumbs([
		{ label: t("admin_root"), href: "/admin" },
		{ label: t("users_title") },
	]);
	const [search, setSearch] = useState("");

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "users", search],
		queryFn: () =>
			adminClient.listAllUsers({
				search,
				pageSize: 50,
			}),
	});

	return (
		<div className="space-y-6">
			<div className="relative max-w-sm">
				<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
				<Input
					placeholder={t("users_search_placeholder")}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="pl-8"
				/>
			</div>

			<div className="rounded-lg border overflow-hidden">
				<Table>
					<TableHeader>
						<TableRow className="bg-muted/30">
							<TableHead>{t("users_col_name")}</TableHead>
							<TableHead>{t("users_col_email")}</TableHead>
							<TableHead>{t("users_col_workspaces")}</TableHead>
							<TableHead>{t("users_col_registered")}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading ? (
							Array.from({ length: 5 }).map((_, i) => (
								<TableRow key={i} className="hover:bg-muted/50">
									<TableCell>
										<Skeleton className="h-4 w-32" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-40" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-24" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-20" />
									</TableCell>
								</TableRow>
							))
						) : data?.users.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={4}
									className="text-center text-muted-foreground"
								>
									{t("users_empty")}
								</TableCell>
							</TableRow>
						) : (
							data?.users.map((user) => (
								<TableRow key={user.id} className="hover:bg-muted/50">
									<TableCell className="font-medium">
										{user.name || "—"}
									</TableCell>
									<TableCell>{user.email || "—"}</TableCell>
									<TableCell>
										<div className="flex flex-wrap gap-1">
											{user.workspaces.slice(0, 3).map((w) => (
												<Badge key={w.workspaceId} variant="secondary">
													{w.workspaceName}
												</Badge>
											))}
											{user.workspaces.length > 3 && (
												<Badge variant="outline">
													+{user.workspaces.length - 3}
												</Badge>
											)}
										</div>
									</TableCell>
									<TableCell>
										{user.createdAt
											? new Date(
													Number(user.createdAt.seconds) * 1000,
												).toLocaleDateString()
											: "—"}
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>

			{data && (
				<div className="text-sm text-muted-foreground">
					{t("users_shown_count", {
						shown: data.users.length,
						total: data.totalCount.toLocaleString(),
					})}
				</div>
			)}
		</div>
	);
}
